import { Key, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Blockquote } from '~/components/ui';
import TrainerSelection from '../Trainer/TrainerSelection';
import { type Slot } from '../Views/TableView/types';

interface Props {
    spaceId: Key;
    /** trainers the grid cell carried; used as-is outside trainer mode */
    trainers?: Slot['trainers'];
    /** the space only sells lessons: a trainer is mandatory and the list must follow the length */
    trainerMode?: boolean;
}

/**
 * Trainer picker for the booking assistant.
 *
 * Outside trainer mode this is the grid cell's frozen trainer list, exactly as before. In trainer
 * mode the list is re-resolved from the server for the times currently in the form: the cell's
 * list goes stale the moment the user changes the length, and rebooking from the booking list has
 * no cell at all.
 */
const BookingTrainerSelection = ({ spaceId, trainers, trainerMode }: Props) => {
    const { t } = useTranslation('space-manager');
    const { watch, setValue } = useFormContext();
    const start: Date | undefined = watch('start');
    const end: Date | undefined = watch('end');
    const trainerId = watch('trainer_id');

    const { data, isLoading } = useSWR<{ data: Slot['trainers'] }>(
        trainerMode && start && end
            ? `space-manager/space/${String(spaceId)}/trainers?start=${new Date(start).toISOString()}&end=${new Date(
                  end
              ).toISOString()}`
            : null
    );

    const resolvedTrainers = useMemo(
        () => (trainerMode ? (data?.data ?? []) : (trainers ?? [])),
        [trainerMode, data, trainers]
    );

    // the picked trainer can fall out of the list when the length changes; leaving the id behind
    // would submit a booking the server rejects with a message about a trainer the user cannot see
    useEffect(() => {
        if (!trainerMode || isLoading || !data || trainerId == null || trainerId === '') return;
        if (resolvedTrainers.some((trainer) => String(trainer.id) === String(trainerId))) return;

        setValue('trainer_id', null, { shouldValidate: true });
    }, [trainerMode, isLoading, data, trainerId, resolvedTrainers, setValue]);

    if (!trainerMode && resolvedTrainers.length <= 0) return null;

    return (
        <div className="tw-mt-4">
            {trainerMode && !isLoading && resolvedTrainers.length <= 0 && (
                <Blockquote className="tw-mb-4">{t('bookingAssistant.noTrainerAvailable')}</Blockquote>
            )}
            <TrainerSelection
                trainers={resolvedTrainers}
                name="trainer_id"
                required={trainerMode}
                label={t('allocation.form.trainerLabel')}
            />
        </div>
    );
};

export default BookingTrainerSelection;
